import { TranslateService } from '@ngx-translate/core';
import { Model } from 'src/app/interfaces/api/model';
import { PostZone, Zone, ZoneColor, ZoneType } from 'src/app/interfaces/api/zone';
import { ZoneService } from 'src/app/services/api/zones/zone.service';
import { ZoneShape } from '../shapes/zone';

const Autodesk = window.Autodesk;

export class FromObjectDockingPanel extends Autodesk.Viewing.UI.DockingPanel {
    viewer: any;
    options: any;
    zonesService: ZoneService;
    translate: TranslateService;

    currentBBox: any;
    currentPolygon: any;
    zones: ZoneShape[] = [];
    currentZone: any;
    currentZones: any[] = [];
    fragIds: number[] = [];
    dbIds: number[] = [];
    currentBBoxes: any;

    selectedFragIds: number[] = [];
    fromObjectPanel: any;
    groupeZones = false;
    colors: any;
    model: Model;
    origin: any;
    offset = { x: 0, y: 0, z: 0 };
    zoneExt: any;
    fragments: any[] = [];
    instanceTree: any;
    geometry: any;
    geometries: any;

    constructor(viewer: any, container: HTMLElement, id: string, title: string, options: any) {
        super(container, id, title, options);

        const titleEl = (this as any).container?.querySelector('.docking-panel-title');
        if (titleEl) {
            titleEl.removeAttribute('data-i18n');
            titleEl.textContent = title;
        }

        this.viewer = viewer;
        this.options = options;

        this.zonesService = options.zonesService;
        this.translate = options.translate;
        this.model = options.model;
        this.origin = this.model.origin;

        const styleId: string = 'fromobject-style';

        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            link.href = 'viewer/zones/fromObject/fromObject.css';
            document.head.appendChild(link);
        }

        let offset = this.viewer.model.getData().globalOffset;

        if (offset)
            this.offset = offset;

        this.zones = options.zones;

        this.colors = {
            "danger": { name: `Danger (${this.translate.instant('colors.red')}`, value: 0xFF0000 },
            "warning": { name: `Warning (${this.translate.instant('colors.orange')}`, value: 0xFFA500 },
            "primary": { name: `Primary (${this.translate.instant('colors.blue')}`, value: 0x0000FF },
            "success": { name: `Success (${this.translate.instant('colors.green')}`, value: 0x008000 },
            "lemon": { name: `Lemon (${this.translate.instant('colors.yellow')}`, value: 0xf3e612 },
            "teal": { name: `Teal (${this.translate.instant('colors.teal')}`, value: 0x39CCCC },
            "fuchsia": { name: `Fuchsia (${this.translate.instant('colors.fuchsia')}`, value: 0xF012BE },
            "info": { name: `Info (${this.translate.instant('colors.lightGrey')}`, value: 0xDDDDDD },
            "dark": { name: `Dark (${this.translate.instant('colors.black')}`, value: 0x000000 },
            "muted": { name: `Muted (${this.translate.instant('colors.grey')}`, value: 0x808080 }
        };

        this.container.classList.add('docking-panel-container-solid-color-a');
        this.container.style.top = "10px";
        this.container.style.right = "10px";
        this.container.style.width = "391px";
        this.container.style.height = "365px";

        const div = document.createElement('div');
        div.style.margin = '0';
        div.style.height = "calc(100% - 60px)"
        div.style.overflowX = "hidden";

        this.container.appendChild(div);

        this.loadAndRenderHtml(div).then(() => {
            this.init();

            let saveBtn = document.getElementById('save-zone');
            if (saveBtn) {
                saveBtn.removeEventListener('click', this.saveZone);
                saveBtn.addEventListener('click', () => this.saveZone());
            }

            let rotateBtn = document.getElementById('zone-rotate');
            if (rotateBtn) {
                rotateBtn.removeEventListener('change', this.onRotateZone);
                rotateBtn.addEventListener('change', (event: Event) => this.onRotateZone(event));
            }

            let groupBtn = document.getElementById('zone-grouped');
            if (groupBtn) {
                groupBtn.removeEventListener('change', (event: Event) => {
                    const checked = (event.target as HTMLInputElement).checked;
                    this.onGroupZone(checked);
                });
                groupBtn.addEventListener('change', (event: Event) => {
                    const checked = (event.target as HTMLInputElement).checked;
                    this.onGroupZone(checked);
                });
            }

            let scaleXBtn = document.getElementById('zone-scale-x');
            if (scaleXBtn) {
                scaleXBtn.removeEventListener('change', this.onScaleXZone);
                scaleXBtn.addEventListener('change', (event: Event) => this.onScaleXZone(event));
            }

            let scaleYBtn = document.getElementById('zone-scale-y');
            if (scaleYBtn) {
                scaleYBtn.removeEventListener('change', this.onScaleYZone);
                scaleYBtn.addEventListener('change', (event: Event) => this.onScaleYZone(event));
            }

            let translateXBtn = document.getElementById('zone-translate-x');
            if (translateXBtn) {
                translateXBtn.removeEventListener('change', this.onTranslateXZone);
                translateXBtn.addEventListener('change', (event: Event) => this.onTranslateXZone(event));
            }

            let translateYBtn = document.getElementById('zone-translate-y');
            if (translateYBtn) {
                translateYBtn.removeEventListener('change', this.onTranslateYZone);
                translateYBtn.addEventListener('change', (event: Event) => this.onTranslateYZone(event));
            }

            let closeBtn = document.querySelector('#zoneFromObjectDockingPanel .docking-panel-close');
            if (closeBtn) {
                closeBtn.removeEventListener('click', this.onClose);
                closeBtn.addEventListener('click', () => this.onClose());
            }
        });

        this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, (event: any) => {
            this.onSelectionChanged();
        });
    }

    init() {
        this.zoneExt = this.viewer.loadedExtensions['ZoneExtension'];

        if (this.zoneExt.dockingPanel)
            this.zoneExt.dockingPanel.setVisible(false);
        if (!this.instanceTree) {
            this.instanceTree = this.viewer.model.getData().instanceTree;
            this.getAllFragments();
        }
    }

    async getAllFragments() {
        try {
            let cbCount = 0;
            const jsData: any[] = [];
            const parents: any[] = [];
            let tree: any;

            const getLeafComponentsRec = async (current: any, parent: any) => {
                try {
                    cbCount++;
                    let childCount = tree.getChildCount(current);

                    if (childCount != 0) {
                        await new Promise(resolve => {
                            tree.enumNodeChildren(current, function (children: any) {
                                childCount--;
                                getLeafComponentsRec(children, current).then(() => {
                                    if (childCount == 0) resolve(true);
                                });
                            }, false);
                        })
                    }

                    if (parents.indexOf(parent) == -1) parents.push(parent);
                    jsData.push({ parent, dbId: current })
                } catch (e) {
                    console.error(e)
                }
            }

            tree = await this.getObjectTree();
            const rootDbId = tree.getRootId();
            await getLeafComponentsRec(rootDbId, 0);
            this.fragments = jsData;
        } catch (e) {
            console.error(e)
        }
    }

    getFragProperties(dbId: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.viewer.model.getProperties(dbId, (objProp: any) => {//expect dbId
                const fragIds: number[] = [];
                let geometries = [];
                let geometry = new THREE.Geometry();
                const box = new Array();
                this.instanceTree.getNodeBox(dbId, box);//An array holding 6 number values: (min-x, min-y, min-z, max-x, max-y, max-z)
                let boundingBox = new THREE.Box3(new THREE.Vector3(box[0], box[1], box[2]), new THREE.Vector3(box[3], box[4], box[5]));

                this.instanceTree.enumNodeFragments(dbId, (fragId: number) => {
                    fragIds.push(fragId)
                })

                fragIds.forEach((fragId: number, i: number) => {
                    const renderProxy = this.viewer.impl.getRenderProxy(this.viewer.model, fragId);
                    const fragProxy = this.viewer.impl.getFragmentProxy(this.viewer.model, fragId);

                    let matrix = new THREE.Matrix4();
                    fragProxy.getAnimTransform();
                    fragProxy.getWorldMatrix(matrix);

                    let geom = new THREE.Geometry();
                    let VE = Autodesk.Viewing.Private.VertexEnumerator;
                    let vertices = [];
                    let indices = [];

                    VE.enumMeshVertices(renderProxy.geometry, (v: any, i: any) => {
                        vertices.push(v.x, v.y, v.z)
                        geom.vertices.push(new THREE.Vector3(v.x, v.y, v.z));
                    });

                    VE.enumMeshIndices(renderProxy.geometry, (a, b, c) => {
                        indices.push(a)
                        indices.push(b)
                        indices.push(c)
                        geom.faces.push(new THREE.Face3(a, b, c));
                    });

                    geom.computeFaceNormals();
                    geometry.merge(geom, matrix);
                })

                geometry.computeBoundingBox();
                resolve({ boundingBox, geometry });
            })
        });
    }

    getGeometryFromFragId(fragIds: number[]): any {
        let geometry = new THREE.Geometry();

        fragIds.forEach((fragId: number, i: number) => {
            const renderProxy = this.viewer.impl.getRenderProxy(this.viewer.model, fragId);
            const fragProxy = this.viewer.impl.getFragmentProxy(this.viewer.model, fragId);

            let matrix = new THREE.Matrix4();
            fragProxy.getAnimTransform();
            fragProxy.getWorldMatrix(matrix);

            let geom = new THREE.Geometry();
            let VE = Autodesk.Viewing.Private.VertexEnumerator;
            let vertices = [];
            let indices = [];

            VE.enumMeshVertices(renderProxy.geometry, (v: any, i: any) => {
                vertices.push(v.x, v.y, v.z)
                geom.vertices.push(new THREE.Vector3(v.x, v.y, v.z));
            });

            VE.enumMeshIndices(renderProxy.geometry, (a, b, c) => {
                indices.push(a)
                indices.push(b)
                indices.push(c)
                geom.faces.push(new THREE.Face3(a, b, c));
            });

            geom.computeFaceNormals();
            geometry.merge(geom, matrix);
        })

        geometry.computeBoundingBox();
        return geometry;
    }

    getObjectTree(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.viewer.getObjectTree((objectTree: any) => {
                resolve(objectTree)
            });
        })
    }

    getLastchildren(id: number): number[] {
        let children: number[] = [];

        const findChild = (current: any) => {
            let currentChildren: number[] = []

            this.fragments.forEach(f => {
                if (f.parent == current) currentChildren.push(f.dbId);
            })

            if (currentChildren.length) {
                currentChildren.forEach(cf => {
                    findChild(cf)
                })
            } else {
                children.push(current)
            }
        }

        findChild(id)
        return children;
    }

    getSelection(ids: number[]): number[] {
        let frags: number[] = []
        let childIds: number[] = []

        ids.forEach((dbId: number) => {
            let children = this.getLastchildren(dbId)
            childIds = childIds.concat(children)
        })

        this.fragments.forEach(f => {
            if (childIds.indexOf(f.dbId) >= 0) frags.push(f.dbId);
        })

        return frags;
    }

    async onSelectionChanged() {
        let selectedFragIds = this.viewer.getSelection();
        let selection = this.fragIds.length ? this.fragIds : this.getSelection(selectedFragIds);

        if (!selection.length) return;

        this.currentBBox = new THREE.Box3()
        this.currentBBoxes = [];
        this.geometry = {};
        let geometry = new THREE.Geometry();
        this.geometries = [];

        for (let fragId of selection) {
            let fragbBox: any = await this.getFragProperties(fragId);

            if (fragbBox.geometry && fragbBox.geometry.vertices.length) {
                if (fragbBox.boundingBox) {
                    this.currentBBoxes.push(fragbBox.boundingBox);
                    this.currentBBox.union(fragbBox.boundingBox);
                }
                geometry.merge(fragbBox.geometry, new THREE.Matrix4());
                this.geometries.push(new THREE.BufferGeometry().fromGeometry(fragbBox.geometry));
            } else {
                let fragGeometry = this.getGeometryFromFragId([fragId]);

                if (fragGeometry.boundingBox) {
                    this.currentBBoxes.push(fragGeometry.boundingBox);
                    this.currentBBox.union(fragGeometry.boundingBox);
                }

                geometry.merge(fragGeometry, new THREE.Matrix4());
                this.geometries.push(new THREE.BufferGeometry().fromGeometry(fragGeometry));
            }
        }

        this.geometry = new THREE.BufferGeometry().fromGeometry(geometry);

        if (this.groupeZones) {
            if (this.currentZone)
                this.currentZone.remove();

            this.currentZone = this.buildZoneFromBoudingBox(this.currentBBox);

            if (this.geometry) {
                let position = this.geometry.getAttribute("position");
                let normal = this.geometry.getAttribute("normal");

                if (position.array.length <= 10000)
                    this.currentZone.geometry = { position: position.array, normal: normal.array };

                this.currentZone.drawGeometry(false);
            }

            this.currentZone.draw(false);
        } else {
            this.currentZones.forEach(z => {
                z.remove();
            });

            this.currentZones = this.buildZonesFromBoudingBoxes(this.currentBBoxes);

            this.currentZones.forEach((z, i) => {
                if (this.geometries && this.geometries[i]) {
                    let position = this.geometries[i].getAttribute("position");
                    let normal = this.geometries[i].getAttribute("normal");

                    if (position.array.length <= 20000) {
                        z.geometry = { position: position.array, normal: normal.array };
                        z.drawGeometry(false);
                    } else {
                        console.error("Too many points in this zones")
                    }
                }

                z.draw(false);
            })
        }
    }

    buildZonesFromBoudingBoxes(boxes: any[]): any[] {
        let zones: any[] = [];

        boxes.forEach((b: any, i: number) => {
            let zone = this.buildZoneFromBoudingBox(b, i);

            zones.push(zone)
        })

        return zones;
    }

    buildZoneFromBoudingBox(box: any, index: number | null = null): any {
        let polygon = [
            [box.max.x, box.max.y],
            [box.max.x, box.min.y],
            [box.min.x, box.min.y],
            [box.min.x, box.max.y]
        ]

        let center = box.center();
        let ztx: any = document.getElementById(`zone-translate-x`);
        ztx.value = center.x;
        let zty: any = document.getElementById(`zone-translate-y`);
        zty.value = center.y;

        let height = box.max.z - box.min.z;
        let z = box.min.z;
        let number = index ? this.zones.length + index : this.zones.length;

        let params = {
            viewer: this.viewer,
            model: this.model,
            center,
            polygon,
            z,
            height,
            name: "Untitled-" + number,
            number,
            color: "danger" as ZoneColor,
            alpha: 0.5,
            visible: true,
            type: "geometry" as ZoneType,
            id: `zone-${number}-${Date.now()}`
        }

        return new ZoneShape(params);
    }

    saveZone() {
        if (this.groupeZones) {
            this.currentZone.updateOrigin();

            const zoneData = this.currentZone.toJSON();
            zoneData.polygon = this.currentZone.getTransformedPoints();
            zoneData.geometry = this.currentZone.getTransformedGeometry();

            if (zoneData.geometry && zoneData.geometry.position && zoneData.geometry.position.length > 9) {
                zoneData.type = "geometry";
            }

            zoneData.z = (this.currentZone.z + this.offset.z) / this.origin.scale + this.origin.position.z;
            zoneData.height = this.currentZone.height / this.model.origin.scale;
            zoneData.type = "geometry";

            this.zonesService.addZone(zoneData as PostZone).subscribe({
                next: (result: any) => {
                    this.onClose();
                },
                error: (err: any) => {
                    console.error(err);
                }
            });
        } else {
            const zones: Zone[] = [];

            this.currentZones.forEach((z, i) => {
                z.updateOrigin();
                const zoneData = z.toJSON();
                zoneData.polygon = z.getTransformedPoints();
                zoneData.geometry = z.getTransformedGeometry();
                zoneData.z = (z.z + this.offset.z) / this.origin.scale + this.origin.position.z;
                zoneData.height = z.height / this.model.origin.scale;
                zoneData.type = "geometry";
                zones.push(zoneData);
            })

            this.zonesService.addZones(zones).subscribe({
                next: (result: any) => {
                    this.onClose();
                },
                error: (err: any) => {
                    console.error(err);
                }
            });
        }
    }

    onGroupZone(value: boolean): void {
        this.groupeZones = value;

        if (this.currentZone)
            this.currentZone.remove();

        this.currentZones.forEach(z => {
            z.remove();
        })

        this.onSelectionChanged();
    }

    onRotateZone(event: Event): void {
        let angle = Number((event.target as HTMLInputElement).value) * Math.PI / 180;

        if (this.currentZone && this.currentZone.mesh)
            this.currentZone.mesh.rotation.z = angle;

        this.currentZones.forEach(z => {
            z.mesh.rotation.z = angle;
        })

        this.viewer.impl.invalidate(true, true, true);
    }

    onScaleXZone(event: Event): void {
        if (this.currentZone && this.currentZone.mesh)
            this.currentZone.mesh.scale.x = (event.target as HTMLInputElement).value;

        this.currentZones.forEach(z => {
            z.mesh.scale.x = (event.target as HTMLInputElement).value;
        })

        this.viewer.impl.invalidate(true, true, true);
    }

    onScaleYZone(event: Event): void {
        if (this.currentZone && this.currentZone.mesh)
            this.currentZone.mesh.scale.y = (event.target as HTMLInputElement).value;

        this.currentZones.forEach(z => {
            z.mesh.scale.y = (event.target as HTMLInputElement).value;
        })

        this.viewer.impl.invalidate(true, true, true);
    }

    onTranslateXZone(event: Event): void {
        if (this.currentZone && this.currentZone.mesh)
            this.currentZone.mesh.position.x = (event.target as HTMLInputElement).value;

        this.currentZones.forEach(z => {
            z.mesh.position.x = (event.target as HTMLInputElement).value;
        })

        this.viewer.impl.invalidate(true, true, true);
    }

    onTranslateYZone(event: Event): void {
        if (this.currentZone && this.currentZone.mesh)
            this.currentZone.mesh.position.y = (event.target as HTMLInputElement).value;

        this.currentZones.forEach(z => {
            z.mesh.position.y = (event.target as HTMLInputElement).value;
        })

        this.viewer.impl.invalidate(true, true, true);
    }

    onClose() {
        this.setVisible(false);

        if (this.currentZone)
            this.currentZone.remove();

        this.currentZones.forEach(z => z.remove());
        this.zoneExt?.init();
        this.zoneExt?.dockingPanel?.setVisible(true);
    }

    async loadAndRenderHtml(div: HTMLDivElement) {
        const template = await this.loadHtmlTemplate('viewer/zones/fromObject/fromObject.html');
        const variables = {

        };

        div.innerHTML = this.renderTemplate(template, variables);
    }

    async loadHtmlTemplate(path: string): Promise<string> {
        const response = await fetch(path);
        return await response.text();
    }

    renderTemplate(template: string, variables: Record<string, string | number>) {
        return template.replace(/{{(.*?)}}/g, (_, key) => String(variables[key.trim()] ?? ''));
    }
}
